import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Product } from "../../../../types/product";
import { useCart } from "../../../../context/CartContext";

interface Props {
  currentProduct: Product;
  allProducts: Product[];
}

const REASON_MAP: Record<string, Record<string, string>> = {
  solar: {
    gas: "Great backup for cloudy days or high-load appliances",
    "whole-home": "Complete your whole-home coverage",
    solar: "Popular pairing — expand your solar capacity",
  },
  gas: {
    solar: "Add silent solar power for everyday use",
    "whole-home": "Upgrade to seamless whole-home protection",
    gas: "Higher wattage option for larger loads",
  },
  "whole-home": {
    solar: "Portable solar to power essentials on the go",
    gas: "Portable backup for garages, workshops & RVs",
    "whole-home": "Parallel-ready for double the capacity",
  },
};

function getBuyerReasonLabel(currentCat: string, suggestedCat: string): string {
  return REASON_MAP[currentCat]?.[suggestedCat] ?? "Frequently bought together";
}

function getPicksForProduct(product: Product, allProducts: Product[]): Product[] {
  const { id, category } = product;

  // Cross-category picks first, then same-category as fallback
  const crossCat = ["solar", "gas", "whole-home"].filter((c) => c !== category) as Product["category"][];

  const crossPicks = crossCat.flatMap((cat) =>
    allProducts
      .filter((p) => p.id !== id && p.category === cat && p.inStock)
      .sort((a, b) => b.rating - a.rating)
      .slice(0, 1)
  );

  const samePicks = allProducts
    .filter((p) => p.id !== id && p.category === category && p.inStock)
    .sort((a, b) => b.rating - a.rating)
    .slice(0, 2);

  const combined = [...crossPicks, ...samePicks];
  const seen = new Set<string>();
  const deduped: Product[] = [];
  for (const p of combined) {
    if (!seen.has(p.id)) {
      seen.add(p.id);
      deduped.push(p);
    }
  }
  return deduped.slice(0, 4);
}

function BoughtCard({ product, currentCategory }: { product: Product; currentCategory: string }) {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [added, setAdded] = useState(false);

  const saveDollars = product.originalPrice ? product.originalPrice - product.price : null;
  const savePct = product.originalPrice
    ? Math.round((1 - product.price / product.originalPrice) * 100)
    : null;
  const reason = getBuyerReasonLabel(currentCategory, product.category);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!product.inStock) return;
    addItem({
      id: product.id,
      name: product.name,
      brand: product.brand,
      price: product.price,
      image: product.image,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const categoryBadge =
    product.category === "solar"
      ? { label: "Solar & Battery", color: "bg-sky-100 text-sky-700" }
      : product.category === "gas"
      ? { label: "Gas Generator", color: "bg-orange-100 text-orange-700" }
      : { label: "Whole-Home", color: "bg-emerald-100 text-emerald-700" };

  return (
    <div
      className="bg-white border border-gray-100 rounded-xl overflow-hidden hover:border-amber-300 transition-all duration-200 cursor-pointer group flex flex-col"
      onClick={() => {
        navigate(`/shop/${product.id}`);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }}
    >
      {/* Image */}
      <div className="relative w-full bg-gray-50 overflow-hidden" style={{ aspectRatio: "4 / 3" }}>
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
        />
        {saveDollars && (
          <span className="absolute top-2.5 left-2.5 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded whitespace-nowrap">
            Save ${saveDollars.toLocaleString()}
          </span>
        )}
        <span className={`absolute top-2.5 right-2.5 text-[10px] font-bold px-2 py-0.5 rounded whitespace-nowrap ${categoryBadge.color}`}>
          {categoryBadge.label}
        </span>
        {/* Popularity pill */}
        <div className="absolute bottom-2.5 left-2.5 flex items-center gap-1 bg-white/90 backdrop-blur-sm border border-amber-100 rounded-md px-1.5 py-1">
          <div className="w-3 h-3 flex items-center justify-center">
            <i className="ri-fire-line text-amber-500 text-xs"></i>
          </div>
          <span className="text-[9px] font-bold text-gray-700 whitespace-nowrap">Frequently Bought</span>
        </div>
      </div>

      {/* Info */}
      <div className="p-4 flex flex-col flex-1">
        <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-widest mb-0.5">{product.brand}</p>
        <h4 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 mb-1.5 flex-1">{product.name}</h4>

        {/* Reason chip */}
        <div className="flex items-start gap-1.5 mb-3">
          <div className="w-3.5 h-3.5 flex items-center justify-center mt-px shrink-0">
            <i className="ri-user-heart-line text-amber-500 text-xs"></i>
          </div>
          <p className="text-[10px] text-gray-500 leading-snug">{reason}</p>
        </div>

        {/* Stars */}
        <div className="flex items-center gap-1 mb-2">
          {[1, 2, 3, 4, 5].map((s) => (
            <i
              key={s}
              className={`text-xs ${s <= Math.floor(product.rating) ? "ri-star-fill text-amber-400" : "ri-star-line text-gray-200"}`}
            ></i>
          ))}
          <span className="text-[10px] text-gray-400 ml-0.5">({product.rating})</span>
        </div>

        {/* Price */}
        <div className="flex items-baseline gap-2 mb-3">
          <span className={`font-extrabold text-base ${product.originalPrice ? "text-red-600" : "text-gray-900"}`}>
            ${product.price.toLocaleString()}
          </span>
          {product.originalPrice && (
            <span className="text-xs text-gray-400 line-through">${product.originalPrice.toLocaleString()}</span>
          )}
          {savePct && (
            <span className="text-xs text-red-500 font-semibold">{savePct}% off</span>
          )}
        </div>

        {/* Trust row */}
        <div className="flex items-center gap-3 mb-3 text-[10px] text-gray-400">
          <span className="flex items-center gap-1">
            <i className="ri-truck-line text-emerald-500"></i> Free Ship
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-shield-check-line text-amber-500"></i> Warranty
          </span>
        </div>

        {/* CTA */}
        <button
          onClick={handleAddToCart}
          disabled={!product.inStock}
          className={`w-full py-2.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
            added
              ? "bg-emerald-500 text-white"
              : product.inStock
              ? "bg-amber-500 hover:bg-amber-400 text-white"
              : "bg-gray-100 text-gray-400 cursor-not-allowed"
          }`}
        >
          {added ? (
            <span className="flex items-center justify-center gap-1.5">
              <i className="ri-check-line"></i> Added to Cart
            </span>
          ) : product.inStock ? (
            <span className="flex items-center justify-center gap-1.5">
              <i className="ri-shopping-cart-2-line"></i> Add to Cart
            </span>
          ) : (
            "Out of Stock"
          )}
        </button>
      </div>
    </div>
  );
}

export default function CustomersAlsoBought({ currentProduct, allProducts }: Props) {
  const picks = getPicksForProduct(currentProduct, allProducts);

  if (picks.length === 0) return null;

  return (
    <section className="mt-14 pt-10 border-t border-gray-100">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center bg-amber-50 border border-amber-200 rounded-xl">
            <i className="ri-shopping-bag-3-line text-amber-600 text-lg"></i>
          </div>
          <div>
            <h2 className="text-lg font-extrabold text-gray-900 leading-tight">Customers Also Bought</h2>
            <p className="text-xs text-gray-400">Popular pairings with this product</p>
          </div>
        </div>
        {/* Bundle badge */}
        <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5">
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className="ri-gift-2-line text-amber-600 text-xs"></i>
          </div>
          <span className="text-[11px] font-bold text-amber-700 whitespace-nowrap">Free shipping on every order</span>
        </div>
      </div>

      {/* Cards grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {picks.map((p) => (
          <BoughtCard key={p.id} product={p} currentCategory={currentProduct.category} />
        ))}
      </div>

      {/* Bottom note */}
      <div className="mt-5 flex items-center gap-2 text-xs text-gray-400">
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-information-line text-gray-300"></i>
        </div>
        <span>Based on purchase patterns from customers who bought this product</span>
      </div>
    </section>
  );
}
